import { createContext, createSignal, type JSX, useContext, createEffect, on } from 'solid-js'
import { type PeriodsState } from '../../features/shared/period-state'
import { PeriodType } from '../../features/shared/period-type'
import {
  getPeriodsByType,
  type SimplePeriodType,
  type CustomDateRange,
  type CompareMode
} from '../helpers/date.functions'
import { useUser } from './user-provider'

export interface EngagementPeriodState {
  selectedPeriod: SimplePeriodType
  customDateRange: CustomDateRange | undefined
  compareMode: CompareMode
  compareCustomRange: CustomDateRange | undefined
}

export interface PeriodContextType {
  periods: () => PeriodsState
  setPeriods: (periods: PeriodsState) => void
  engagementPeriod: () => EngagementPeriodState
  setEngagementPeriod: (state: Partial<EngagementPeriodState>) => void
}

const DEFAULT_PERIODS = (): PeriodsState => ({
  type: PeriodType.Last7Days,
  ...getPeriodsByType(PeriodType.Last7Days)
})

const DEFAULT_ENGAGEMENT_PERIOD = (): EngagementPeriodState => ({
  selectedPeriod: 'last_30_days',
  customDateRange: undefined,
  compareMode: 'previous_period',
  compareCustomRange: undefined
})

const [periods, setPeriods] = createSignal<PeriodsState>(DEFAULT_PERIODS(), {
  equals: (a, b) => a.type === b.type && a.current.from === b.current.from && a.current.to === b.current.to
})

const [engagementPeriod, _setEngagementPeriod] = createSignal<EngagementPeriodState>(DEFAULT_ENGAGEMENT_PERIOD(), {
  equals: (a, b) =>
    a.selectedPeriod === b.selectedPeriod &&
    a.compareMode === b.compareMode &&
    a.customDateRange?.from === b.customDateRange?.from &&
    a.customDateRange?.to === b.customDateRange?.to &&
    a.compareCustomRange?.from === b.compareCustomRange?.from &&
    a.compareCustomRange?.to === b.compareCustomRange?.to
})

const updatePeriods = (periods: PeriodsState): void => {
  setPeriods(periods)
}

const updateEngagementPeriod = (state: Partial<EngagementPeriodState>): void => {
  _setEngagementPeriod((prev) => ({ ...prev, ...state }))
}

const PeriodContext = createContext<PeriodContextType>({
  periods,
  setPeriods: updatePeriods,
  engagementPeriod,
  setEngagementPeriod: updateEngagementPeriod
})

export const PeriodProvider = (props: { children: JSX.Element }): JSX.Element => {
  const { organizationSlug } = useUser()

  createEffect(
    on(
      () => organizationSlug(),
      (slug, prevSlug) => {
        if (!slug) return
        // Only reset if the organization actually changed and it's not the initial load
        if (prevSlug && slug !== prevSlug) {
          setPeriods(DEFAULT_PERIODS())
          _setEngagementPeriod(DEFAULT_ENGAGEMENT_PERIOD())
        }
      }
    )
  )

  const store: PeriodContextType = {
    periods,
    setPeriods: updatePeriods,
    engagementPeriod,
    setEngagementPeriod: updateEngagementPeriod
  }

  return <PeriodContext.Provider value={store}>{props.children}</PeriodContext.Provider>
}

export const usePeriods = (): PeriodContextType => {
  return useContext(PeriodContext)
}
